import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { useJsonLd, usePageMeta } from "@/hooks/usePageMeta";
import { articles } from "@/mocks/articles";
import { topics } from "@/mocks/topics";

export default function TopicsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTopic = searchParams.get("topic") || "all";
  const initialQuery = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(initialQuery);

  usePageMeta({
    title: "爆料专题 - 选美吃瓜内定黑幕与评分猫腻全部分类 | 吃瓜深扒社",
    description:
      "按评分猫腻、幕后交易、爆冷瞬间、网络票选与合约规则五个方向浏览选美吃瓜全部内容，支持关键词搜索，快速定位你想复核的那场赛果争议。",
    keywords: "选美吃瓜专题,选美吃瓜内定,选美吃瓜黑幕,选美吃瓜评委,评分猫腻",
    path: "/topics",
  });

  useJsonLd("topics", {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        "@id": "https://dstcm.cn/topics#collectionpage",
        url: "https://dstcm.cn/topics",
        name: "选美吃瓜爆料专题",
        inLanguage: "zh-CN",
        description:
          "选美吃瓜全部深度解析内容，按评分猫腻、幕后交易、爆冷瞬间、网络票选与合约规则分类。",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://dstcm.cn/" },
          { "@type": "ListItem", position: 2, name: "爆料专题", item: "https://dstcm.cn/topics" },
        ],
      },
      {
        "@type": "ItemList",
        name: "选美吃瓜深度解析列表",
        itemListElement: articles.slice(0, 10).map((article, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: article.title,
          url: `https://dstcm.cn/article/${article.id}`,
        })),
      },
    ],
  });

  const applyTopic = (topicId: string) => {
    const next = new URLSearchParams(searchParams);
    if (topicId === "all") {
      next.delete("topic");
    } else {
      next.set("topic", topicId);
    }
    setSearchParams(next);
  };

  const handleSearch = (value: string) => {
    setKeyword(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set("q", value.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    return articles.filter((article) => {
      const matchTopic = activeTopic === "all" || article.category === activeTopic;
      const matchKey =
        !key ||
        article.title.toLowerCase().includes(key) ||
        article.excerpt.toLowerCase().includes(key) ||
        article.tags.some((tag) => tag.toLowerCase().includes(key));
      return matchTopic && matchKey;
    });
  }, [activeTopic, keyword]);

  const activeTopicMeta = topics.find((topic) => topic.id === activeTopic);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: "爆料专题" },
          ]}
        />

        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
            <span className="flex items-center gap-2 font-label text-xs tracking-[0.2em] text-primary-600">
              <i className="ri-file-search-line" aria-hidden="true"></i>
              ALL STORIES
            </span>
            <h1 className="mt-3 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              选美吃瓜爆料专题
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-loose text-foreground-700">
              这里汇总了全部赛果解析内容。每篇文章都会先走一遍评分口径三步核对法，把可核查的事实与仍存疑的推测分开标注，你可以按方向筛选，也可以用关键词直接搜索。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <label htmlFor="topic-search" className="mb-2 block font-label text-sm text-foreground-800">
                    关键词搜索
                  </label>
                  <div className="relative">
                    <i
                      className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"
                      aria-hidden="true"
                    ></i>
                    <input
                      id="topic-search"
                      type="text"
                      value={keyword}
                      onChange={(event) => handleSearch(event.target.value)}
                      placeholder="如：同分、评委、票数"
                      className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <nav aria-label="专题分类" className="mt-5 rounded-lg border border-background-200 bg-background-50 p-3">
                  <h2 className="px-3 py-2 font-heading text-base font-bold text-foreground-950">内容方向</h2>
                  <ul className="mt-1 flex flex-col">
                    <li>
                      <button
                        type="button"
                        onClick={() => applyTopic("all")}
                        className={[
                          "flex w-full items-center justify-between rounded-md px-3 py-2.5 font-label text-sm transition-colors",
                          activeTopic === "all"
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-700 hover:bg-background-100",
                        ].join(" ")}
                      >
                        <span className="flex items-center gap-2">
                          <i className="ri-apps-2-line" aria-hidden="true"></i>
                          全部内容
                        </span>
                        <span className="text-xs">{articles.length}</span>
                      </button>
                    </li>
                    {topics.map((topic) => {
                      const count = articles.filter((article) => article.category === topic.id).length;
                      return (
                        <li key={topic.id}>
                          <button
                            type="button"
                            onClick={() => applyTopic(topic.id)}
                            className={[
                              "flex w-full items-center justify-between rounded-md px-3 py-2.5 font-label text-sm transition-colors",
                              activeTopic === topic.id
                                ? "bg-primary-500 text-background-50"
                                : "text-foreground-700 hover:bg-background-100",
                            ].join(" ")}
                          >
                            <span className="flex items-center gap-2">
                              <i className={topic.icon} aria-hidden="true"></i>
                              {topic.name}
                            </span>
                            <span className="text-xs">{count}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </nav>

                {activeTopicMeta && (
                  <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-5">
                    <h2 className="font-heading text-base font-bold text-accent-950">
                      {activeTopicMeta.name}
                    </h2>
                    <p className="mt-2 text-sm leading-relaxed text-accent-900">
                      {activeTopicMeta.description}
                    </p>
                  </div>
                )}
              </div>
            </aside>

            <div className="lg:col-span-9">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
                <p className="text-sm text-foreground-700">
                  共 <strong className="font-semibold text-primary-600">{filtered.length}</strong> 篇内容
                  {activeTopic !== "all" && activeTopicMeta ? ` · ${activeTopicMeta.name}` : ""}
                  {keyword.trim() ? ` · 关键词「${keyword.trim()}」` : ""}
                </p>
                {(activeTopic !== "all" || keyword.trim()) && (
                  <button
                    type="button"
                    onClick={() => {
                      setKeyword("");
                      setSearchParams(new URLSearchParams());
                    }}
                    className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 font-label text-xs text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" aria-hidden="true"></i>
                    重置筛选
                  </button>
                )}
              </div>

              {filtered.length > 0 ? (
                <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {filtered.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                  <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true"></i>
                  <h2 className="mt-4 font-heading text-lg font-bold text-foreground-900">
                    没有匹配到内容
                  </h2>
                  <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                    换一个关键词试试，比如「同分」「评委」「票数」，或者点击下方按钮查看全部内容。
                  </p>
                  <Link
                    to="/topics"
                    className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
                  >
                    <i className="ri-refresh-line" aria-hidden="true"></i>
                    查看全部内容
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}